import { CanonicalDataType } from "@/shared/domain/topics";
import { LoggerService } from "@/shared/services";

import type { ColumnFiltersAction } from "./actions";
import {
  ALL_COLUMNS_ID,
  ALL_COLUMNS_NAME,
  LOG_TIME_ID,
  LOG_TIME_NAME,
} from "./columnDefinition";
import {
  type ColumnFilter,
  type ColumnFiltersState,
  isExclusivityViolation,
} from "./state";

function newColumnFilter(stages: ColumnFilter["stages"]): ColumnFilter {
  return { stages, combinator: "or" };
}

export function columnFiltersReducer(
  state: ColumnFiltersState,
  action: ColumnFiltersAction,
): ColumnFiltersState {
  const next: ColumnFiltersState = { filters: {}, columns: {} };
  switch (action.type) {
    case "set_columns":
      // Drop filters whose columns are gone from the new set, but always keep
      // the synthetic "All columns" and "Log time" entries.
      next.columns[ALL_COLUMNS_ID] = {
        columnId: ALL_COLUMNS_ID,
        name: ALL_COLUMNS_NAME,
      };
      next.columns[LOG_TIME_ID] = {
        columnId: LOG_TIME_ID,
        name: LOG_TIME_NAME,
        dataType: CanonicalDataType.Timestamp,
      };
      if (Object.hasOwn(state.filters, ALL_COLUMNS_ID)) {
        next.filters[ALL_COLUMNS_ID] = state.filters[ALL_COLUMNS_ID];
      }
      if (Object.hasOwn(state.filters, LOG_TIME_ID)) {
        next.filters[LOG_TIME_ID] = state.filters[LOG_TIME_ID];
      }
      for (const column of action.payload) {
        next.columns[column.columnId] = column;
        if (Object.hasOwn(state.filters, column.columnId)) {
          next.filters[column.columnId] = state.filters[column.columnId];
        }
      }
      return next;
    case "delete_filter":
      // Preserve columns from the old state.
      // Clear the given filter by ID from the new state.
      next.columns = { ...state.columns };
      next.filters = { ...state.filters };
      delete next.filters[action.payload.columnId];
      return next;
    case "set_filter":
      // Design choice: __all__ (pan-column search) and per-column filters are
      // mutually exclusive. Users can search across all columns OR apply granular
      // per-column conditions with chained stages, but not both simultaneously.
      // Allowing both would significantly increase complexity (interaction between
      // global and column-specific match semantics, unclear highlighting behavior,
      // confusing filter pill layout) for a marginal use case. The dropdown in
      // FilterBar hides conflicting options and shows a helper message; this
      // guard is defense-in-depth.
      if (isExclusivityViolation(action.payload.columnId, state)) {
        return state;
      }
      // Preserve columns from the old state.
      // Set a specific stage by index iff it matches a known column.
      next.columns = { ...state.columns };
      next.filters = { ...state.filters };
      if (Object.hasOwn(state.columns, action.payload.columnId)) {
        const existing = state.filters[action.payload.columnId];
        const stages = existing ? [...existing.stages] : [];
        // undefined → append; otherwise clamp to prevent sparse array holes
        const idx = action.payload.stageIndex ?? stages.length;
        const clampedIndex = Math.min(idx, stages.length);
        stages[clampedIndex] = {
          value: action.payload.value,
          condition: action.payload.condition,
          ...(action.payload.value2 !== undefined && {
            value2: action.payload.value2,
          }),
        };
        next.filters[action.payload.columnId] = existing
          ? { ...existing, stages }
          : newColumnFilter(stages);
      } else {
        LoggerService.warn(
          `set_filter: unknown column ID "${action.payload.columnId}"`,
        );
      }
      return next;
    case "delete_stage": {
      // Remove a single stage; if the stages array becomes empty, remove the filter.
      next.columns = { ...state.columns };
      next.filters = { ...state.filters };
      const existing = state.filters[action.payload.columnId];
      if (existing) {
        const stages = existing.stages.filter(
          (_, i) => i !== action.payload.stageIndex,
        );
        if (stages.length === 0) {
          delete next.filters[action.payload.columnId];
        } else {
          next.filters[action.payload.columnId] = { ...existing, stages };
        }
      }
      return next;
    }
    case "set_combinator": {
      next.columns = { ...state.columns };
      next.filters = { ...state.filters };
      const existing = state.filters[action.payload.columnId];
      if (existing) {
        next.filters[action.payload.columnId] = {
          ...existing,
          combinator: action.payload.combinator,
        };
      }
      return next;
    }
    case "clear_filters":
      // Preserve columns from the old state.
      // Clear all filters.
      next.columns = { ...state.columns };
      return next;
    default: {
      const _exhaustive: never = action;
      return _exhaustive;
    }
  }
}
